import { type AuthState, toRelativeUrl } from '@okta/okta-auth-js';
import { useOktaAuth } from '@okta/okta-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  removeReqInterceptor,
  setupAuthReqInterceptor,
} from '../../interceptors/auth-header.interceptor';
// FullStory - commented out: import { FullStorySetUser } from '../../services/full-story-set-user.service';
import { Auth } from './interfaces/auth.interface';
import { LoggedUser } from './interfaces/logged-user.interface';

export const AuthContext = createContext<Auth>({});

const parseLoggedUser = (authState: AuthState): LoggedUser | undefined => {
  if (authState?.idToken?.claims === undefined) return undefined;
  const { name, email, groups, preferred_username } = authState.idToken.claims;
  if (name === undefined) return;
  const user: LoggedUser = {
    name: name,
    email: email ?? preferred_username ?? name,
    groups,
  };
  return user;
};
let reqInterceptorId: number | undefined = undefined;
let isRenewing = false;
//let resInterceptorId:number | undefined = undefined
export const AuthProvider = ({ children }: { children: any }) => {
  const { oktaAuth, authState } = useOktaAuth();
  const [user, setUser] = useState<LoggedUser>();
  const [authenticated, setAuthenticated] = useState<boolean | undefined>();

  useEffect(() => {
    oktaAuth.tokenManager.on('expired', function (key) {
      if (key === 'accessToken' && !isRenewing) {
        isRenewing = true;

        const getNewToken = async () => {
          //const token = await oktaAuth.tokenManager.get('refreshToken')
          const token = await oktaAuth.tokenManager.renew('accessToken');
          const accessToken = oktaAuth.getAccessToken();

          removeReqInterceptor(reqInterceptorId);
          reqInterceptorId = setupAuthReqInterceptor(accessToken);
          isRenewing = false;
          return token;
        };
        getNewToken();
      }
    });
    // TODO: Check was this function should do
    // Triggered when a token has been renewed
    oktaAuth.tokenManager.on('renewed', function () {});
    // TODO: Check was this function should do
    // Triggered when an OAuthError is returned via the API (typically during token renew)
    oktaAuth.tokenManager.on('error', function () {});
  }, [oktaAuth]);

  const logout = useCallback(() => {
    oktaAuth?.signOut();
  }, [oktaAuth]);
  const login = useCallback(async () => {
    const isAuthenticated = await oktaAuth.isAuthenticated();
    if (isAuthenticated === true || isAuthenticated === undefined) return;
    const originalUri = toRelativeUrl(
      window.location.href,
      window.location.origin,
    );

    if (originalUri === '/login/callback') {
      return;
    }
    oktaAuth.setOriginalUri(originalUri);
    //console.log('SignInWithRedirect')
    oktaAuth.signInWithRedirect();
  }, [oktaAuth]);
  useEffect(() => {
    if (authState?.isAuthenticated === undefined) return;
    if (authState?.isAuthenticated === true) {
      const user = parseLoggedUser(authState);
      if (user) {
        // FullStory - commented out: FullStorySetUser(user);
      }
      setUser(() => user);
    } else {
      setUser(undefined);
    }
    setAuthenticated(authState?.isAuthenticated);
    removeReqInterceptor(reqInterceptorId);
    reqInterceptorId = setupAuthReqInterceptor(
      authState.accessToken?.accessToken,
    );
  }, [authState]);

  const value = useMemo(
    (): Auth => ({
      authenticated,
      user,
      logout,
      login,
      oktaAuth,
    }),
    [user, logout, login, authenticated, oktaAuth],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): Auth => {
  return useContext(AuthContext);
};
